import { CATEGORY_DISPLAY_MODE_1 } from "@constants/index";

import { DisplayStyleValues } from "./DisplayStyleSettings";

// Sous-ensemble du designStore lu pour calculer l'héritage / l'override de
// l'étape produit composé (ESS-3857). Le snapshot Valtio le satisfait
// structurellement.
export interface CompositeDisplaySource {
    productDisplayMode: string;
    productDisplayBackgroundColor: string;
    productDisplayBackgroundOpacity: number;
    productDisplaySelectedTextColor: string;
    productDisplayBorderColor: string;
    productDisplayBorderStyle: string;
    productDisplaySvgBorder: string;
    productDisplaySvgBorderName: string;
    productDisplayMode1BackgroundColor: string;
    productDisplayMode1BackgroundOpacity: number;
    productDisplayMode1SelectedTextColor: string;
    productDisplayMode1BorderColor: string;
    productDisplayMode1BorderStyle: string;
    productDisplayMode1SvgBorder: string;
    productDisplayMode1SvgBorderName: string;
    compositeProductDisplayOverride: boolean;
    compositeProductDisplayMode: string;
    // Jeu override Mode 2 (historique).
    compositeProductDisplayBackgroundColor: string;
    compositeProductDisplayBackgroundOpacity: number;
    compositeProductDisplaySelectedTextColor: string;
    compositeProductDisplayBorderColor: string;
    compositeProductDisplayBorderStyle: string;
    compositeProductDisplaySvgBorder: string;
    compositeProductDisplaySvgBorderName: string;
    // Jeu override Mode 1 (indépendant du jeu Mode 2).
    compositeProductDisplayMode1BackgroundColor: string;
    compositeProductDisplayMode1BackgroundOpacity: number;
    compositeProductDisplayMode1SelectedTextColor: string;
    compositeProductDisplayMode1BorderColor: string;
    compositeProductDisplayMode1BorderStyle: string;
    compositeProductDisplayMode1SvgBorder: string;
    compositeProductDisplayMode1SvgBorderName: string;
}

// Clé persistée du mode d'affichage PROPRE à l'étape (ESS-3857).
export const COMPOSITE_PRODUCT_MODE_PERSIST_KEY = "compositeProductDisplayMode";

// Clés persistées (compositeProduct) du jeu override Mode 2.
export const COMPOSITE_PRODUCT_MODE2_PERSIST_KEYS: Record<
    keyof DisplayStyleValues,
    string
> = {
    backgroundColor: "compositeProductDisplayBackgroundColor",
    backgroundOpacity: "compositeProductDisplayBackgroundOpacity",
    selectedTextColor: "compositeProductDisplaySelectedTextColor",
    borderColor: "compositeProductDisplayBorderColor",
    borderStyle: "compositeProductDisplayBorderStyle",
    svgBorder: "compositeProductDisplaySvgBorder",
    svgBorderName: "compositeProductDisplaySvgBorderName",
};

// Clés persistées (compositeProduct) du jeu override Mode 1.
export const COMPOSITE_PRODUCT_MODE1_PERSIST_KEYS: Record<
    keyof DisplayStyleValues,
    string
> = {
    backgroundColor: "compositeProductDisplayMode1BackgroundColor",
    backgroundOpacity: "compositeProductDisplayMode1BackgroundOpacity",
    selectedTextColor: "compositeProductDisplayMode1SelectedTextColor",
    borderColor: "compositeProductDisplayMode1BorderColor",
    borderStyle: "compositeProductDisplayMode1BorderStyle",
    svgBorder: "compositeProductDisplayMode1SvgBorder",
    svgBorderName: "compositeProductDisplayMode1SvgBorderName",
};

// Jeu de clés persist du mode donné (Mode 1 / Mode 2).
export function getCompositePersistKeys(
    mode: string
): Record<keyof DisplayStyleValues, string> {
    return mode === CATEGORY_DISPLAY_MODE_1
        ? COMPOSITE_PRODUCT_MODE1_PERSIST_KEYS
        : COMPOSITE_PRODUCT_MODE2_PERSIST_KEYS;
}

// Mode d'affichage EFFECTIF de l'étape produit composé : le mode local si
// override actif, sinon le mode global. Propre à l'étape — ne pilote jamais le
// mode global.
export function getEffectiveCompositeProductMode(
    src: CompositeDisplaySource
): string {
    return src.compositeProductDisplayOverride
        ? src.compositeProductDisplayMode
        : src.productDisplayMode;
}

// Valeurs produit GLOBALES = valeurs héritées par défaut dans l'étape produit
// composé. Le jeu (Mode 1 / Mode 2) est choisi par `mode` (défaut : mode global)
// — un appelant peut passer un mode explicite pour semer depuis le bon jeu.
export function getInheritedProductDisplay(
    src: CompositeDisplaySource,
    mode: string = src.productDisplayMode
): DisplayStyleValues {
    const isMode1 = mode === CATEGORY_DISPLAY_MODE_1;
    return isMode1
        ? {
              backgroundColor: src.productDisplayMode1BackgroundColor,
              backgroundOpacity: src.productDisplayMode1BackgroundOpacity,
              selectedTextColor: src.productDisplayMode1SelectedTextColor,
              borderColor: src.productDisplayMode1BorderColor,
              borderStyle: src.productDisplayMode1BorderStyle,
              svgBorder: src.productDisplayMode1SvgBorder,
              svgBorderName: src.productDisplayMode1SvgBorderName,
          }
        : {
              backgroundColor: src.productDisplayBackgroundColor,
              backgroundOpacity: src.productDisplayBackgroundOpacity,
              selectedTextColor: src.productDisplaySelectedTextColor,
              borderColor: src.productDisplayBorderColor,
              borderStyle: src.productDisplayBorderStyle,
              svgBorder: src.productDisplaySvgBorder,
              svgBorderName: src.productDisplaySvgBorderName,
          };
}

// Valeurs override LOCALES de l'étape pour le `mode` donné. L'étape stocke un
// jeu de valeurs par mode (Mode 1 / Mode 2), indépendants — comme l'interface
// générique globale. Basculer le mode montre/édite le jeu correspondant.
export function getCompositeOverrideValues(
    src: CompositeDisplaySource,
    mode: string
): DisplayStyleValues {
    return mode === CATEGORY_DISPLAY_MODE_1
        ? {
              backgroundColor: src.compositeProductDisplayMode1BackgroundColor,
              backgroundOpacity:
                  src.compositeProductDisplayMode1BackgroundOpacity,
              selectedTextColor:
                  src.compositeProductDisplayMode1SelectedTextColor,
              borderColor: src.compositeProductDisplayMode1BorderColor,
              borderStyle: src.compositeProductDisplayMode1BorderStyle,
              svgBorder: src.compositeProductDisplayMode1SvgBorder,
              svgBorderName: src.compositeProductDisplayMode1SvgBorderName,
          }
        : {
              backgroundColor: src.compositeProductDisplayBackgroundColor,
              backgroundOpacity: src.compositeProductDisplayBackgroundOpacity,
              selectedTextColor: src.compositeProductDisplaySelectedTextColor,
              borderColor: src.compositeProductDisplayBorderColor,
              borderStyle: src.compositeProductDisplayBorderStyle,
              svgBorder: src.compositeProductDisplaySvgBorder,
              svgBorderName: src.compositeProductDisplaySvgBorderName,
          };
}

// Valeurs EFFECTIVES de l'étape produit composé : le jeu override local du mode
// effectif s'il est activé, sinon les valeurs héritées du global. Source unique
// partagée par le panneau de réglages et la preview (cohérence garantie).
export function getEffectiveCompositeProductDisplay(
    src: CompositeDisplaySource
): DisplayStyleValues {
    if (!src.compositeProductDisplayOverride) {
        return getInheritedProductDisplay(src);
    }
    return getCompositeOverrideValues(
        src,
        getEffectiveCompositeProductMode(src)
    );
}
